import { z } from "zod";
import {
  getAttachmentContract,
  getTaskContract,
  listTaskFilesContract,
  readTaskChatContract,
} from "@project/shared/tool-contracts";
import { defineTool } from "../harness/index.js";
import { defineContractTool } from "./contract-tool.js";
import type { AgentConnection } from "../connection/agent-connection.js";
import { textResult, imageBlock, isImageMimeType, formatCliEvent } from "./helpers.js";

type ContentBlock =
  | { type: "text"; text: string }
  | { type: "image"; data: string; mimeType: string };

export function buildReadTaskChatTool(connection: AgentConnection) {
  return defineContractTool(
    readTaskChatContract,
    async ({ limit, task_id }) => {
      try {
        const messages = await connection.call("getChatMessages", {
          sessionId: connection.sessionId,
          limit,
          taskId: task_id,
        });
        return textResult(JSON.stringify(messages, null, 2));
      } catch {
        return textResult(
          JSON.stringify({
            note: "Could not fetch live chat. Chat history was provided in the initial context.",
          }),
        );
      }
    },
    { annotations: { readOnlyHint: true } },
  );
}

export function buildGetCurrentPlanTool(connection: AgentConnection) {
  return defineTool(
    "get_current_plan",
    "Re-read the current task's plan. Use when the user updated the plan or asked you to re-read it — otherwise the plan is already in initial context. For task metadata use get_task.",
    {},
    async () => {
      try {
        const ctx = await connection.call("getTaskContext", {
          sessionId: connection.sessionId,
        });
        return textResult(ctx.plan ?? "No plan available.");
      } catch {
        return textResult("Could not fetch updated plan.");
      }
    },
    { annotations: { readOnlyHint: true } },
  );
}

export function buildGetTaskTool(connection: AgentConnection) {
  return defineContractTool(
    getTaskContract,
    async ({ slug_or_id }) => {
      try {
        const task = await connection.call("getTask", {
          sessionId: connection.sessionId,
          taskSlugOrId: slug_or_id,
        });
        return textResult(JSON.stringify(task, null, 2));
      } catch (error) {
        return textResult(
          `Failed to get task: ${error instanceof Error ? error.message : "Unknown error"}`,
        );
      }
    },
    { annotations: { readOnlyHint: true } },
  );
}

export function buildGetExecutionLogsTool(connection: AgentConnection) {
  return defineTool(
    "get_execution_logs",
    "Read CLI execution logs — agent reasoning, tool calls, and setup/dev-server output. Filter via source='agent' or 'application'. For human chat use read_task_chat.",
    {
      task_id: z
        .string()
        .optional()
        .describe(
          "Task ID or slug. Omit to read logs from the current task. Only the current task or one of its child tasks can be read.",
        ),
      source: z
        .enum(["agent", "application"])
        .optional()
        .describe("Filter by log source. Omit for all logs."),
      limit: z
        .number()
        .optional()
        .describe("Max number of log entries to return (default 50, max 500)."),
    },
    async ({ task_id, source, limit }) => {
      try {
        const effectiveLimit = Math.min(limit ?? 50, 500);
        const result = await connection.call("getCliHistory", {
          sessionId: connection.sessionId,
          limit: effectiveLimit,
          taskId: task_id,
          source,
        });
        const formatted = (result as Array<Record<string, unknown>>)
          .map((entry) => {
            const type = entry.type as string;
            const time = entry.timestamp as string;
            return `[${time}] [${type}] ${formatCliEvent(entry as Record<string, unknown>)}`;
          })
          .join("\n");
        return textResult(formatted || "No CLI logs found.");
      } catch (error) {
        return textResult(
          `Failed to fetch CLI logs: ${error instanceof Error ? error.message : "Unknown error"}`,
        );
      }
    },
    { annotations: { readOnlyHint: true } },
  );
}

export function buildListTaskFilesTool(connection: AgentConnection) {
  return defineContractTool(
    listTaskFilesContract,
    async () => {
      try {
        const files = await connection.call("getTaskFiles", {
          sessionId: connection.sessionId,
        });
        const metadata = files.map((file) => {
          const { content: _c, ...rest } = file;
          return rest;
        });
        const content: ContentBlock[] = [
          { type: "text" as const, text: JSON.stringify(metadata, null, 2) },
        ];
        for (const file of files) {
          if (file.content && file.contentEncoding === "base64" && isImageMimeType(file.mimeType)) {
            content.push(imageBlock(file.content, file.mimeType));
          }
        }
        return { content };
      } catch {
        return textResult("Failed to list task files.");
      }
    },
    { annotations: { readOnlyHint: true } },
  );
}

export function buildGetAttachmentTool(connection: AgentConnection) {
  return defineContractTool(
    getAttachmentContract,
    async ({ fileId }) => {
      try {
        const file = await connection.call("getTaskFile", {
          sessionId: connection.sessionId,
          fileId,
        });
        const { content: rawContent, ...metadata } = file;
        const content: ContentBlock[] = [
          { type: "text" as const, text: JSON.stringify(metadata, null, 2) },
        ];
        if (rawContent && file.contentEncoding === "base64" && isImageMimeType(file.mimeType)) {
          content.push(imageBlock(rawContent, file.mimeType));
        } else if (rawContent) {
          content[0] = { type: "text" as const, text: JSON.stringify(file, null, 2) };
        }
        return { content };
      } catch (error) {
        return textResult(
          `Failed to get task file: ${error instanceof Error ? error.message : "Unknown error"}`,
        );
      }
    },
    { annotations: { readOnlyHint: true } },
  );
}

/** All read-only task context tools. */
export function buildTaskContextTools(connection: AgentConnection) {
  return [
    buildReadTaskChatTool(connection),
    buildGetCurrentPlanTool(connection),
    buildGetTaskTool(connection),
    buildGetExecutionLogsTool(connection),
    buildListTaskFilesTool(connection),
    buildGetAttachmentTool(connection),
  ];
}
